import { useState } from 'react';
import { Download, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { demoApi } from '../../api/demo';

/**
 * Bottone di export delle revisioni di un run demo.
 *
 * Il download e' costruito interamente nel browser con `Blob` e
 * `URL.createObjectURL`: nessun file viene scritto lato server. L'object URL
 * viene revocato subito dopo il click, altrimenti ogni export trattiene in
 * memoria una copia del payload per tutta la vita del documento (l'errore
 * classico di questo componente).
 *
 * In errore non scarica nulla: mostra il messaggio e lascia i dati correnti
 * della pagina intatti.
 */

interface DemoExportButtonProps {
  runId: string;
}

function downloadJson(payload: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Errore durante l\'esportazione delle revisioni';
}

export function DemoExportButton({ runId }: DemoExportButtonProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const payload = await demoApi.exportRun(runId);
      downloadJson(payload, `vetmail-demo-${runId}.json`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleExport}
        disabled={isExporting}
      >
        {isExporting ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Download className="size-3.5" />
        )}
        Esporta revisioni
      </Button>
      {error && (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}
